// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { withStyles, Grid, Button } from '@material-ui/core';
import { func, object, arrayOf, bool, number, shape } from 'prop-types';
import { push } from 'connected-react-router';
import { capitalize, get } from 'lodash';

import ListContainer from '../../list';
import DetailsContainer from '../../details';
import { fetchProducts, fetchTiers, saveTiers } from '../../../actions/products';
import {
  getErrors,
  getIsError,
  getIsLoading,
  getIsTiersSaveSuccess,
  getProducts,
  getProductsListTotalElements,
  getProductsListTotalPages,
  getTiers,
} from '../../../reducers/products';
import { getUserDetails } from '../../../reducers/portalConfig';
import { getI18n, getI18nFormattedMessage } from '../../../utils/intl';
import styles from './styles';
import {
  DetailsHeader,
  Footer,
  TabPanel,
  TabsContainer,
} from '../../../components';
import { FilterByName, FilterByAccessType } from './filters';
import { getTabIndex, hasCRUDPermissions } from '../../../utils';
import { PERMISSION_PRODUCT, PERMISSION_TIER, PRODUCT_ACCESS_TYPE_ALL } from '../../../constants';
import { Tiers } from './Tiers';

const GRID_ROWS_PER_PAGE_DEFAULT_OPTION = 24;
const GRID_ROWS_PER_PAGE_OPTIONS = [24, 48, 96];

export const getProductListColumns = (intl) => [
  {
    id: 'name',
    label: intl.getI18nFormattedMessage('label.product.list.column.name'),
    link: '/admin/products/details',
  },
  {
    id: 'description',
    label: intl.getI18nFormattedMessage(
      'label.product.list.column.description',
    ),
    value: (item) => item.description,
    isCellOverflow: true,
  },
  {
    id: 'accessStatus',
    label: intl.getI18nFormattedMessage('label.access.type'),
    value: (item) => capitalize(item.accessStatus),
  },
];

const getTabItems = (isVisibleTier) => [
  {
    id: "overview",
    tabId: 'products-list-tab',
    label: getI18nFormattedMessage('label.product.list.title'),
    visible: true,
  },
  {
    id: "tiers",
    tabId: 'products-list-tiers-tab',
    label: getI18nFormattedMessage('label.product.tiers.title'),
    visible: isVisibleTier,
  },
];

const getTabs = (tabItems) => tabItems.filter(({ visible }) => visible);

export const ProductsList = (props) => {
  const {
    products,
    push,
    classes,
    fetchProducts,
    isLoading,
    totalElements,
    totalPages,
    user,
    pageErrors,
    isError,
  } = props;

  useEffect(() => {
    if (!hasCRUDPermissions(user, ['READ'], PERMISSION_PRODUCT)) {
      props.push('/404');
    }
  }, [user]);

  // Tab logic
  const isVisibleTier= hasCRUDPermissions(user, ['READ'], PERMISSION_TIER);
  const hasAddProduct = hasCRUDPermissions(user, ['CREATE'], PERMISSION_PRODUCT);
  const tabItems = getTabItems(isVisibleTier);
  const tabs = getTabs(tabItems, isVisibleTier);
  const defaultIndex = getTabIndex(tabItems, props.match);
  const [tabIndex, setTabIndex] = useState(defaultIndex);
  const [currentTab, setCurrentTab] = useState(get(tabs[defaultIndex], 'tabId'));
  const [accessTypeFilterValue, setAccessTypeFilterValue] = useState(PRODUCT_ACCESS_TYPE_ALL);

  useEffect(() => {
    const tabName = get(props.match, 'params.tabName');
    if(tabName === 'tiers' && !isVisibleTier) {
      props.push('/404');
    } else {
      const defaultIndex = getTabIndex(tabs, props.match);
      setTabIndex(defaultIndex);
      setCurrentTab(tabs[defaultIndex].tabId);
    }
  }, [props.match]);
  const handleTabChange = (e, newValue) => {
    setTabIndex(newValue);
    setCurrentTab(tabs[newValue].tabId);
    if (tabs[newValue].id === 'overview') push(`/admin/products`);
    else push(`/admin/products/${tabs[newValue].id}`);
  };

  const intl = getI18n(useIntl());
  const [rowsPerPage, setRowsPerPage] = useState(
    GRID_ROWS_PER_PAGE_DEFAULT_OPTION,
  );
  const [page, setPage] = useState(0);
  const onChangePreviousPage = () => {
    onChangePage(page - 1);
  };
  const onChangeNextPage = () => {
    onChangePage(page + 1);
  };

  const [errors, setErrors] = useState([]);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');

  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  useEffect(() => {
    fetchProducts({ page, size : rowsPerPage });
  }, []);

  useEffect(() => {
    setErrors(isError ? pageErrors : []);
  }, [isError, pageErrors]);

  const [nameFilterValue, setNameFilterValue] = useState('');

  useEffect(() => {
    applyFilter();
  }, [page, rowsPerPage, accessTypeFilterValue]);

  useEffect(() => {
    !nameFilterValue && applyFilter();
  }, [nameFilterValue])

  const onChangePage = (newPage) => {
    setPage(newPage);
  };

  const onChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const onFilterByAccessType = (value) => {
    setAccessTypeFilterValue(value);
    setPage(0);
  }

  const applyFilter = () => {
    fetchProducts({
      page,
      size: rowsPerPage,
      ...(nameFilterValue && {
        name: nameFilterValue,
      }),
      ...(accessTypeFilterValue !== PRODUCT_ACCESS_TYPE_ALL && {
        accessStatus: accessTypeFilterValue,
      }),
    });
  }

  const renderFilters = () => (
    <>
      <FilterByName
        fieldContainerClass={classes.fieldContainer}
        name={intl.getI18nMessage('label.filter')}
        value={nameFilterValue}
        placeholder={intl.getI18nMessage('label.rate.quota.name')}
        handleChange={(value) => {
          setNameFilterValue(value);
        }}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            setPage(0);
            fetchProducts({
              page: 0,
              size: rowsPerPage,
              ...(nameFilterValue && {
                name: nameFilterValue,
              }),
            });
          }
        }}
      />
      <FilterByAccessType
        fieldContainerClass={classes.fieldContainer}
        name={intl.getI18nMessage('label.filter')}
        value={accessTypeFilterValue}
        handleChange={onFilterByAccessType}
        hideLabel
      />
      <Grid item className={classes.actionContainer} md={6} sm={6} xs={12}>
        {hasAddProduct &&
          <Button
            variant="contained"
            color="secondary"
            onClick={() => push('/admin/products/details')}
            id="products-list-add"
            data-apim-test="products-list-add"
            data-layer7-test="products-list-add"
          >
            {intl.getI18nMessage('label.product.add')}
          </Button>
        }
      </Grid>
    </>
  );

  return (
    <div
      className={classes.content}
      id="products-page"
      data-layer7-test="products-page"
    >
      <DetailsContainer
        detailsPageId="products-list-page"
        isLoading={isLoading}
        classes={classes}
        notificationId="products-list-notifications"
        notificationStatus={notificationStatus}
        setNotificationStatus={setNotificationMessage}
        notificationMessage={notificationMessage}
        setNotificationMessage={setNotificationMessage}
        notificationContainerClass={classes.notificationContainer}
        hasNotificationIcon={true}
        errors={errors}
        header={
          <DetailsHeader
            headerId="products-list-header-container"
            type="textField"
            name={intl.getI18nMessage('label.product.list.title')}
            subTitle={intl.getI18nMessage('label.product.list.description')}
            isFieldEdit={false}
            classes={classes}
            showDelete={false}
          />
        }
        tabs={
          <TabsContainer
            id="products-list-tab-container"
            tabValue={tabIndex}
            orientation="horizontal"
            tabItems={tabs}
            handleTabChange={handleTabChange}
            tabActiveClass={classes.tabActive}
            tabIndicatorClass={classes.tabIndicator}
          />
        }
        page={
          <>
            <TabPanel
              visible={currentTab === "products-list-tab"}
              id="products-list-tab-panel"
              classes={classes.tabPanel}
            >
              <ListContainer
                listPageId="products-page"
                isLoading={isLoading}
                pageBodyClass={classes.pageBody}
                filterAndSortContent={renderFilters()}
                columns={getProductListColumns(intl) || []}
                rows={products}
                noResultsMessage={
                  nameFilterValue
                    ? intl.getI18nMessage(
                        'label.product.list.no.results.search',
                      )
                    : intl.getI18nMessage('label.product.list.no.results')
                }
                page={page}
                pageFilterAndSortClass={classes.pageFilterAndSort}
                rowsPerPageOptions={GRID_ROWS_PER_PAGE_OPTIONS}
                totalElements={totalElements}
                totalPages={totalPages}
                rowsPerPage={rowsPerPage}
                onChangeRowsPerPage={onChangeRowsPerPage}
                onChangePage={onChangePage}
                onChangePreviousPage={onChangePreviousPage}
                onChangeNextPage={onChangeNextPage}
              />
            </TabPanel>
            <TabPanel
              visible={currentTab === "products-list-tiers-tab"}
              id="products-list-tiers-tab-panel"
            >
              <Tiers
                {...props}
                isCurrentTab={currentTab === 'products-list-tiers-tab'}
                notifyMessages={notifyMessages}
              />
            </TabPanel>
          </>
        }
        footer={<Footer />}
      />
    </div>
  );
};

ProductsList.propTypes = {
  products: arrayOf(object),
  classes: object,
  fetchProducts: func,
  fetchTiers: func,
  saveTiers: func,
  push: func,
  isLoading: bool,
  totalElements: number,
  totalPages: number,
  user: object,
  isError: bool,
  pageErrors: arrayOf(object),
  match: shape({}),
};

const mapStateToProps = (state) => ({
  products: getProducts(state),
  isLoading: getIsLoading(state),
  totalElements: getProductsListTotalElements(state),
  totalPages: getProductsListTotalPages(state),
  user: getUserDetails(state),
  isError: getIsError(state),
  pageErrors: getErrors(state),
  isTiersSaveSuccess: getIsTiersSaveSuccess(state),
  tiers: getTiers(state),
});

const mapDispatchToProps = {
  push,
  fetchProducts,
  fetchTiers,
  saveTiers,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(ProductsList);
